import {
  AssignmentLateOutlined,
  DeleteOutline,
  PendingActions,
  PublishedWithChangesOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import { Box, Button, Chip, Tooltip } from "@mui/material";
import { amber, blue, green, grey, orange, red } from "@mui/material/colors";
import { isArray, isBoolean, isEmpty, isNil, isObject, size } from "lodash";
import { memo, Suspense, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import AssociationsDisplay from "../../../../components/associations/AssociationsDisplay";
import AssociationsEditor from "../../../../components/associations/AssociationsEditor";
import { ASSOCIATION_FIELD_MAP } from "../../../../components/associations/config/associationFieldsConfig";
import JsonPreview from "../../../../components/common/JsonPreview";
import LoanConditionCommentInput from "../../../../components/comments/LoanConditionCommentInput";
import CreateUpdateLoanConditionForm from "../../../../components/forms/CreateUpdateLoanConditionForm";
import HtmlEditor from "../../../../components/html/HtmlEditor";
import Flx from "../../../../components/layout/Flx";
import LoadingFallback from "../../../../components/LoadingFallback";
import FileUploadProcess from "../../../../components/processes/FileUploadProcess";
import CommentsRenderer from "../../../../components/renderers/CommentsRenderer";
import DocumentIdsRenderer from "../../../../components/renderers/DocumentIdsRenderer";
import DetailRow from "../../../../components/sidebar/shared/DetailRow";
import SidebarSection from "../../../../components/sidebar/shared/SidebarSection";
import { FIELD_TYPE } from "../../../../components/sidebar/shared/sidebarFieldUtils";
import LoanFilesCompactTable from "../../../../components/tables/LoanFilesCompactTable";
import Txt from "../../../../components/typography/Txt";
import { useChecklistRouteHook } from "../../../../hooks/useChecklistRouteHook";
import { useUnderwritingHook } from "../../../../hooks/useUnderwritingHook";
import { loanDrilldownSet } from "../../../../redux/actions/loanDrilldownActions";
import { sidebarClear } from "../../../../redux/actions/sidebarActions";
import { loanFetchedDataUwRequirementsSet } from "../../../../redux/reducers/loanFetchedDataReducer";
import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { htmlStringToText } from "../../../../_src_shared/utils/functions/htmlStringToText";
import { conditionTitle } from "../../../../utils/conditions/conditionTitle";
import { getUserFullNameFromEmail } from "../../../../utils/functions/getUserFullNameFromEmail";
import DrilldownSidebarPane from "../DrilldownSidebarPane";
import SectionCard from "../../../../components/layout/SectionCard";
import DetailRowLabel from "../../../../components/sidebar/shared/DetailRowLabel";
import { getDaysAgoFormatted } from "../../../../_src_shared/utils/functions/getDaysAgoFormatted";
import { getDaysAgo } from "../../../../_src_shared/utils/functions/getDaysAgo";
import { formatDateShort } from "../../../../utils/dates/formatDateShort";
import { getConditionGateLabel } from "../../../../constants/selectOptions/selectOptionsConditionGate";

const OBJECT_TYPE_ITEM_LABEL = "Loan Condition";

const DrilldownSidebarAddEditLoanConditionRedesign = memo(() => {
  const conditionState = useSelector((state) => state.sidebar?.state);
  const uwRequirements = useSelector(
    (state) => state.loanFetchedData?.uwRequirements,
  );
  const condition = useMemo(() => {
    const filteredCondition = uwRequirements?.find(
      (c) => c._id === conditionState?._id,
    );
    if (isNil(filteredCondition)) {
      return;
    }
    return filteredCondition;
  }, [conditionState, uwRequirements]);

  const isUpdating = useMemo(
    () => isObject(condition) && !isEmpty(condition) && !!condition?._id,
    [condition],
  );
  // Create-mode seed: a sidebar-state object WITHOUT an `_id` is prefill for a
  // NEW condition (e.g. "Create condition from this document" pre-attaches the
  // viewed document via uploadedDocumentIds + associations.loanDocuments).
  const prefillValues = useMemo(
    () =>
      !isUpdating &&
      isObject(conditionState) &&
      !isEmpty(conditionState) &&
      !conditionState?._id
        ? conditionState
        : null,
    [isUpdating, conditionState],
  );

  return (
    <Suspense fallback={<LoadingFallback />}>
      <DrilldownSidebarPane
        title={
          isUpdating ? OBJECT_TYPE_ITEM_LABEL : `Add ${OBJECT_TYPE_ITEM_LABEL}`
        }
        icon={<PendingActions className="thin" />}
        initialWidth="620px"
        bodyPadding={0}
        headerSx={{ pb: 1 }}
        titleBottomContent={
          isUpdating ? (
            <ConditionSummaryBanner conditionData={condition} />
          ) : null
        }
      >
        <SidebarContent
          isUpdating={isUpdating}
          condition={condition}
          prefillValues={prefillValues}
        />
      </DrilldownSidebarPane>
    </Suspense>
  );
});

const SidebarContent = ({ isUpdating, condition, prefillValues }) => {
  const dispatch = useDispatch();
  const handleCreateSuccess = useCallback(() => {
    dispatch(sidebarClear());
  }, [dispatch]);

  if (!isUpdating) {
    return (
      <Box sx={{ p: 3, overflowY: "auto", height: "100%" }}>
        <CreateUpdateLoanConditionForm
          initialValues={prefillValues}
          onSuccessFn={handleCreateSuccess}
        />
      </Box>
    );
  }
  return <ManageCondition condition={condition} />;
};

/* ═══════════════════════════════════════════
   MANAGE MODE — overview / edit / files / json
   ═══════════════════════════════════════════ */

const TABS = {
  OVERVIEW: "overview",
  EDIT: "edit",
  FILES: "files",
  ASSOCIATIONS: "associations",
  COMMENTS: "comments",
  JSON: "json",
};

const ManageCondition = ({ condition }) => {
  const [tab, setTab] = useState(TABS.OVERVIEW);
  const uwRequirements = useSelector(
    (state) => state.loanFetchedData?.uwRequirements,
  );
  const conditionData = useMemo(
    () => uwRequirements?.find((c) => c._id === condition?._id) || condition,
    [uwRequirements, condition],
  );
  const fileCount = size(conditionData?.uploadedDocumentIds) || 0;
  const commentCount =
    size(conditionData?.comments || conditionData?.conditionComments) || 0;
  const associationCount = useMemo(() => {
    const associations = conditionData?.associations;
    if (!associations) return 0;
    return Object.values(associations).reduce(
      (acc, list) => acc + (isArray(list) ? list.length : 0),
      0,
    );
  }, [conditionData?.associations]);

  const tabs = useMemo(
    () => [
      { key: TABS.OVERVIEW, label: "Overview" },
      { key: TABS.EDIT, label: "Edit" },
      { key: TABS.FILES, label: `Files${fileCount ? ` (${fileCount})` : ""}` },
      {
        key: TABS.ASSOCIATIONS,
        label: `Associations${associationCount ? ` (${associationCount})` : ""}`,
      },
      {
        key: TABS.COMMENTS,
        label: `Comments${commentCount ? ` (${commentCount})` : ""}`,
      },
      { key: TABS.JSON, label: "JSON" },
    ],
    [fileCount, commentCount, associationCount],
  );

  return (
    <Flx column sx={{ flexGrow: 1, height: "100%", overflow: "hidden" }}>
      <Box
        sx={{
          px: 2.5,
          pt: 0.5,
          pb: 0,
          background: "#ffffff",
          borderBottom: `1px solid ${grey[200]}`,
        }}
      >
        {/* <ConditionSummaryBanner conditionData={conditionData} /> */}
        <TabStrip tabs={tabs} active={tab} onChange={setTab} />
      </Box>

      <Box sx={{ flexGrow: 1, overflowY: "auto", background: "#fafbfc" }}>
        {tab === TABS.OVERVIEW && <OverviewTab conditionData={conditionData} />}
        {tab === TABS.COMMENTS && (
          <Box sx={{ px: 2.5, py: 2 }}>
            <CollapsibleCard>
              <ConditionCommentsBlock
                defaultOpen
                conditionData={conditionData}
              />
            </CollapsibleCard>
          </Box>
        )}
        {tab === TABS.FILES && (
          <Box sx={{ px: 2.5, py: 2 }}>
            <CollapsibleCard>
              <ConditionFilesBlock defaultOpen conditionData={conditionData} />
            </CollapsibleCard>
          </Box>
        )}
        {tab === TABS.ASSOCIATIONS && (
          <Box sx={{ px: 2.5, py: 2 }}>
            <CollapsibleCard>
              <ConditionAssociationsBlock
                defaultOpen
                conditionData={conditionData}
              />
            </CollapsibleCard>
          </Box>
        )}
        {tab === TABS.EDIT && (
          <Box sx={{ px: 2.5, py: 2 }}>
            <CreateUpdateLoanConditionForm
              isUpdating
              initialValues={conditionData}
            />
          </Box>
        )}

        {tab === TABS.JSON && (
          <Box sx={{ p: 2 }}>
            <JsonPreview values={conditionData} defaultCollapse={3} />
          </Box>
        )}
      </Box>
    </Flx>
  );
};

/* ─── Condition banner (status chip + title preview) ─── */

const STATUS_TONE = {
  Open: { fg: grey[700], bg: grey[100] },
  "In Progress": { fg: amber[900], bg: amber[50] },
  "Second Level Review": { fg: orange[900], bg: amber[50] },
  Accepted: { fg: green[800], bg: green[50] },
  Rejected: { fg: red[800], bg: red[50] },
};

const ConditionSummaryBanner = ({ conditionData }) => {
  const status = conditionData?.status || "Unknown";
  const tone = STATUS_TONE[status] || { fg: grey[700], bg: grey[100] };
  const title = conditionTitle(conditionData);

  return (
    <Flx column g={1} sx={{ p: 2.2, pt: 0 }}>
      <Txt
        sx={{
          fontSize: 12.5,
          // fontSize: 13,
          color: "#0f172a",
          fontWeight: 400,
          lineHeight: 1.4,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {title}
      </Txt>
      <Flx
        ac
        g={1}
        wrap
        sx={{
          ".MuiSvgIcon-root": {
            fontSize: `13px !important`,
            ml: `6px !important`,
          },
        }}
      >
        <Chip
          label={status}
          size="small"
          sx={{
            height: 22,
            fontSize: 11,
            fontWeight: 600,
            color: tone.fg,
            background: tone.bg,
            border: `1px solid ${tone.bg}`,
          }}
        />
        {conditionData?.conditionGate && (
          <Chip
            label={getConditionGateLabel(conditionData.conditionGate)}
            size="small"
            sx={{
              height: 22,
              fontSize: 11,
              background: blue[50],
              color: blue[900],
            }}
          />
        )}
        {conditionData?.requirementType && (
          <>
            {conditionData.requirementType === "Document" && (
              <Chip
                label={
                  conditionData.docStipStatus
                    ? conditionData.docStipStatus === "Accepted"
                      ? "Required Document Accepted"
                      : `Requires Document - ${conditionData.docStipStatus}`
                    : "Requires Document"
                }
                size="small"
                sx={{
                  height: 22,
                  fontSize: 11,
                  fontWeight:
                    conditionData.docStipStatus === "Accepted" ? 500 : 400,
                  background:
                    conditionData.docStipStatus === "Accepted"
                      ? green[50]
                      : blue[50],
                  color:
                    conditionData.docStipStatus === "Accepted"
                      ? green[900]
                      : blue[900],
                }}
              />
            )}
          </>
        )}

        {(conditionData?.resolvedBy || conditionData?.resolutionNotes) && (
          <Chip
            icon={
              <PublishedWithChangesOutlined
                sx={{ color: `#4da153 !important` }}
              />
            }
            label="Resolved"
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              fontSize: 11,
              background: "#e8f5e966",
              color: green[900],
              border: `1px solid ${green[400]}`,
            }}
          />
        )}
        {conditionData?.associatedTaskListItemId && (
          <Chip
            icon={<AssignmentLateOutlined />}
            label="Created From Checklist"
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              fontSize: 11,
              background: red[50],
            }}
          />
        )}
        {conditionData?.displayExternally && (
          <Chip
            icon={<VisibilityOutlined />}
            label="External Facing"
            size="small"
            variant="outlined"
            sx={{
              height: 22,
              fontSize: 11,
              background: amber[50],
            }}
          />
        )}
        <AssociationChips associations={conditionData?.associations} />
      </Flx>
    </Flx>
  );
};

const AssociationChips = ({ associations }) => {
  const loan = useSelector((state) => state.loanDrilldown);
  const entries = useMemo(() => {
    if (!associations) return [];
    return Object.entries(associations)
      .map(([key, ids]) => {
        const field = ASSOCIATION_FIELD_MAP[key];
        const count = isArray(ids) ? ids.length : 0;
        if (!field || !count) return null;
        const source = field.getSource?.(loan) || [];
        const items = ids
          .map((id) => {
            const rec = source.find((r) => r?._id === id);
            if (!rec) return null;
            return {
              id,
              label: field.getLabel?.(rec) || "Unnamed",
              sublabel: field.getSublabel?.(rec) || "",
            };
          })
          .filter(Boolean);
        return { key, field, count, items };
      })
      .filter(Boolean);
  }, [associations, loan]);

  if (isEmpty(entries)) return null;

  return entries.map(({ key, field, count, items }) => {
    const Icon = field.icon;
    return (
      <Tooltip
        key={key}
        arrow
        placement="top"
        slotProps={{
          tooltip: {
            sx: {
              background: "#0f172a",
              maxWidth: 320,
              p: 1,
            },
          },
          arrow: { sx: { color: "#0f172a" } },
        }}
        title={
          <Flx column g={0.75} sx={{ py: 0.25 }}>
            <Flx ac g={0.75} sx={{ opacity: 0.85 }}>
              {Icon && <Icon sx={{ fontSize: 12, color: field.color }} />}
              <Txt
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textTransform: "uppercase",
                  color: "#e2e8f0",
                }}
              >
                {field.label} ({count})
              </Txt>
            </Flx>
            {isEmpty(items) ? (
              <Txt sx={{ fontSize: 11, color: grey[400], fontStyle: "italic" }}>
                Unable to resolve items on this loan
              </Txt>
            ) : (
              <Flx column g={0.5}>
                {items.map((it) => (
                  <Flx key={it.id} column g={0}>
                    <Txt
                      sx={{
                        fontSize: 11.5,
                        color: "#f8fafc",
                        lineHeight: 1.3,
                      }}
                    >
                      {it.label}
                    </Txt>
                    {it.sublabel && (
                      <Txt
                        sx={{
                          fontSize: 10.5,
                          color: grey[400],
                          lineHeight: 1.3,
                        }}
                      >
                        {it.sublabel}
                      </Txt>
                    )}
                  </Flx>
                ))}
              </Flx>
            )}
          </Flx>
        }
      >
        <Chip
          icon={
            Icon ? <Icon sx={{ color: `${field.color} !important` }} /> : null
          }
          label={`${field.label} (${count})`}
          size="small"
          variant="outlined"
          sx={{
            height: 22,
            fontSize: 11,
            borderColor: grey[300],
            color: grey[800],
            cursor: "default",
          }}
        />
      </Tooltip>
    );
  });
};

const TabStrip = ({ tabs, active, onChange }) => (
  <Flx ac g={0} sx={{ mx: -0.5 }}>
    {tabs.map((t) => {
      const isActive = active === t.key;
      return (
        <Box
          key={t.key}
          component="button"
          onClick={() => onChange(t.key)}
          sx={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            px: 1.5,
            py: 1,
            fontSize: 12,
            fontWeight: isActive ? 600 : 500,
            color: isActive ? blue[700] : grey[600],
            borderBottom: `2px solid ${isActive ? blue[500] : "transparent"}`,
            transition: "color 0.1s",
            "&:hover": { color: blue[700] },
          }}
        >
          {t.label}
        </Box>
      );
    })}
  </Flx>
);

/* ─── OVERVIEW TAB ─── */

const OverviewTab = ({ conditionData }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const users = useSelector((state) => state.users);
  const loanDocuments = loanDrilldown?.loanDocuments;
  const loanId = loanDrilldown?._id;

  const statusFields = useMemo(() => {
    const fields = [
      {
        label: "Status",
        value: conditionData?.status,
        fieldType: FIELD_TYPE.EDITABLE,
      },
      {
        label: "Type",
        value: getConditionGateLabel(conditionData?.conditionGate),
        fieldType: FIELD_TYPE.EDITABLE,
      },
    ];
    fields.push({
      label: "Required Document",
      value:
        (conditionData?.requirementType === "Document" &&
          (conditionData?.docType || conditionData?.docGroup)) ||
        "None",
      fieldType: FIELD_TYPE.EDITABLE,
    });
    if (conditionData?.docStipStatus) {
      fields.push({
        label: "Document Status",
        value: conditionData.docStipStatus,
        fieldType: FIELD_TYPE.EDITABLE,
      });
    }
    if (conditionData?.dueDate || conditionData?.dueDateTime) {
      fields.push({
        label: "Due Date",
        value: formatDate(conditionData?.dueDate || conditionData?.dueDateTime),
        fieldType: FIELD_TYPE.EDITABLE,
      });
    }
    return fields;
  }, [conditionData]);

  const metaFields = useMemo(
    () => [
      {
        label: "Created",
        value: conditionData?.commentDateTime
          ? formatDateTime(conditionData.commentDateTime)
          : null,
        fieldType: FIELD_TYPE.READ_ONLY,
      },
      {
        label: "Created By",
        value: getUserFullNameFromEmail(users, conditionData?.createdBy),
        fieldType: FIELD_TYPE.READ_ONLY,
      },
      {
        label: "Last Updated",
        value: conditionData?.updateDateTime
          ? formatDateTime(conditionData.updateDateTime)
          : null,
        fieldType: FIELD_TYPE.READ_ONLY,
      },
      {
        label: "Last Updated By",
        value: getUserFullNameFromEmail(users, conditionData?.updatedBy),
        fieldType: FIELD_TYPE.READ_ONLY,
      },
      {
        label: "Auto Generated",
        value: isBoolean(conditionData?.autoGenerated)
          ? conditionData.autoGenerated
            ? "Yes"
            : "No"
          : null,
        fieldType: FIELD_TYPE.READ_ONLY,
      },
      {
        label: "Display Externally",
        value: isBoolean(conditionData?.displayExternally)
          ? conditionData.displayExternally
            ? "Yes"
            : "No"
          : null,
        fieldType: FIELD_TYPE.READ_ONLY,
      },
    ],
    [conditionData, users],
  );

  const hasResolution =
    conditionData?.resolutionNotes || conditionData?.resolvedBy;

  return (
    <Flx column g={0} sx={{ px: 2.5, py: 2 }}>
      <CollapsibleCard>
        <SidebarSection title="Status & Assignment" underlinePosition="title">
          {statusFields.map((f) => (
            <DetailRow
              key={f.label}
              label={f.label}
              value={f.value}
              editable={f.fieldType === FIELD_TYPE.EDITABLE}
            />
          ))}
          {conditionData?.updateDateTime && (
            <DetailRow
              label={"Last Updated"}
              // value={getDaysAgo(conditionData.updateDateTime)}
              value={`${formatDateShort(conditionData.updateDateTime)} (${getDaysAgoFormatted(conditionData.updateDateTime)})`}
              editable={false}
            />
          )}
        </SidebarSection>
      </CollapsibleCard>

      <CollapsibleCard>
        <SidebarSection
          title="Requirement Details"
          underlinePosition="title"
          // bodySx={{ px: 0 }}
        >
          {conditionData?.docType ? (
            <Flx column g={1.5}>
              <Txt sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}>
                {conditionData.docType}
              </Txt>
              {conditionData?.htmlString && (
                <HtmlEditor
                  value={conditionData.htmlString}
                  suppressOuterBorder
                  readOnly
                />
              )}
            </Flx>
          ) : (
            <Box sx={{ px: 0.5, py: 1 }}>
              <HtmlEditor
                value={conditionData?.htmlString}
                suppressOuterBorder
                readOnly
              />
            </Box>
          )}
        </SidebarSection>
      </CollapsibleCard>

      {hasResolution && (
        <CollapsibleCard
          sx={{ borderLeft: `3px solid ${green[200]} !important` }}
        >
          <SidebarSection title="Resolution" underlinePosition="title">
            <Box sx={{ px: 0.5, py: 1 }}>
              <Flx column g={0.2}>
                <DetailRowLabel>Resolved By</DetailRowLabel>
                <Txt
                  sx={{
                    fontSize: 11.5,
                    color: "#334155",
                    whiteSpace: "pre-wrap",
                    lineHeight: 1.5,
                  }}
                >
                  {getUserFullNameFromEmail(
                    users,
                    conditionData.resolvedBy,
                    "Unknown user",
                  )}
                </Txt>
              </Flx>
              {conditionData.resolutionNotes && (
                <Flx column g={0.2} sx={{ pt: 2 }}>
                  <DetailRowLabel>Resolution Notes</DetailRowLabel>
                  <Txt
                    sx={{
                      fontSize: 11.5,
                      color: "#334155",
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.5,
                    }}
                  >
                    {htmlStringToText(conditionData.resolutionNotes)}
                  </Txt>
                </Flx>
              )}
            </Box>
          </SidebarSection>
        </CollapsibleCard>
      )}
      <CollapsibleCard>
        <ConditionCommentsBlock conditionData={conditionData} />
      </CollapsibleCard>
      <CollapsibleCard>
        <ConditionFilesBlock conditionData={conditionData} />
      </CollapsibleCard>
      <CollapsibleCard>
        <ConditionAssociationsBlock conditionData={conditionData} />
      </CollapsibleCard>
      <CollapsibleCard>
        <SidebarSection
          title="Metadata"
          underlinePosition="title"
          // defaultOpen={false}
        >
          {metaFields.map((f) => (
            <DetailRow key={f.label} label={f.label} value={f.value} />
          ))}
        </SidebarSection>
      </CollapsibleCard>

      <Box sx={{ mt: 2 }}>
        <DeleteConditionSection
          loanId={loanId}
          requirementId={conditionData?._id}
        />
      </Box>
    </Flx>
  );
};

/* ─── Shared section blocks (used by Overview tab + dedicated tabs) ─── */

const ConditionCommentsBlock = ({ defaultOpen, conditionData }) => {
  const loanId = useSelector((state) => state.loanDrilldown?._id);
  const comments = conditionData?.comments || conditionData?.conditionComments;
  return (
    <SidebarSection
      title={`Comments (${size(comments)})`}
      underlinePosition="title"
      defaultOpen={defaultOpen ?? !isEmpty(comments)}
    >
      <Flx column g={1.5}>
        {!isEmpty(comments) && (
          <CommentsRenderer
            onlyComments
            hideHeader
            hideIfEmpty
            commentThreadTopMargin={0}
            comments={comments}
          />
        )}
        <LoanConditionCommentInput
          loanId={loanId}
          commentThreadTopMargin={0.1}
          conditionId={conditionData?._id}
          inputTypeBasic
        />
      </Flx>
    </SidebarSection>
  );
};

const ConditionFilesBlock = ({ defaultOpen, conditionData }) => (
  <SidebarSection
    title={`Related Files (${size(conditionData?.uploadedDocumentIds)})`}
    underlinePosition="title"
    defaultOpen={defaultOpen ?? !isEmpty(conditionData?.uploadedDocumentIds)}
  >
    <ManageConditionFilesTab data={conditionData} />
  </SidebarSection>
);

const ConditionAssociationsBlock = ({ defaultOpen, conditionData }) => {
  const loanId = useSelector((state) => state.loanDrilldown?._id);
  const associationCount = useMemo(() => {
    const associations = conditionData?.associations;
    if (!associations) return 0;
    return Object.values(associations).reduce(
      (acc, list) => acc + (isArray(list) ? list.length : 0),
      0,
    );
  }, [conditionData?.associations]);
  return (
    <SidebarSection
      title={`Associations (${associationCount})`}
      underlinePosition="title"
      defaultOpen={defaultOpen ?? false}
      bodySx={{ pt: 1, pb: 2 }}
    >
      <ConditionAssociationsEditor dealId={loanId} condition={conditionData} />
    </SidebarSection>
  );
};

/* ─── FILES TAB ─── */

const ManageConditionFilesTab = ({ data }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanDocuments = loanDrilldown?.loanDocuments;

  const files = useMemo(() => {
    return loanDocuments?.filter((doc) =>
      data?.uploadedDocumentIds?.includes(doc._id),
    );
  }, [loanDocuments, data]);
  return (
    <>
      <Flx
        // sx={{ overflow: "auto", flexGrow: 1, height: "100%" }}
        column
        g={1.5}
      >
        <UploadFileToLoanConditionProcess
          loanId={loanDrilldown?._id}
          conditionId={data?._id}
          docGroup={data?.docGroup}
          docType={data?.docType}
        />
        {isEmpty(files) ? null : (
          <LoanFilesCompactTable files={files} hideIfEmpty />
        )}
      </Flx>
    </>
    // </Flx>
  );
};

const UploadFileToLoanConditionProcess = ({
  loanId,
  conditionId,
  docType,
  docGroup,

  keyName,
}) => {
  const { loading, uploadFilesToUnderwritingRequirement } =
    useChecklistRouteHook();
  const { axiosGetIsLoading, axiosPostIsLoading, getLoanById } =
    useUnderwritingHook();
  const dispatch = useDispatch();

  const handleOnSubmit = (f, { onSuccessFn } = {}) => {
    uploadFilesToUnderwritingRequirement({
      dealId: loanId,
      requirementId: conditionId,
      files: f,
      onSuccessFn: (d) => {
        getLoanById({
          loanId,
          onSuccessFn: (data) => {
            dispatch(loanDrilldownSet(data));
            dispatch(loanFetchedDataUwRequirementsSet(d));
            onSuccessFn?.();
          },

          // onFailFn: () => dispatch(loanDrilldownSet({})),
        });
      },
    });
  };
  return (
    <Flx column g={0.5}>
      {/* <Txt>Relevant Documents</Txt> */}
      <FileUploadProcess
        loanId={loanId}
        docType={docType}
        borderSize={2}
        uploadHeight={"80px"}
        disableCategoryAndTypeSelection
        // disableCategoryAndTypeSelectionCount
        docGroup={docGroup}
        categoryAndTypeLabel={`Select Document Category`}
        keyName={keyName}
        onSubmit={handleOnSubmit}
        loading={loading || axiosGetIsLoading || axiosPostIsLoading}
      />
    </Flx>
  );
};

/* ─── Associations editor (same behavior as legacy) ─── */

const ConditionAssociationsEditor = ({ dealId, condition }) => {
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const { loading: saving, updateLoanUnderwritingRequirement } =
    useChecklistRouteHook();
  const [editing, setEditing] = useState(false);

  const handleSave = useCallback(
    (newAssociations) => {
      if (!condition?._id) return;
      updateLoanUnderwritingRequirement({
        dealId,
        requirementId: condition._id,
        data: { associations: newAssociations },
        onSuccessMsg: "Associations updated",
        onSuccessFn: (response) => {
          let nextConditions = null;
          if (isArray(response)) nextConditions = response;
          else if (isArray(response?.conditions))
            nextConditions = response.conditions;
          else if (isArray(response?.comments))
            nextConditions = response.comments;

          if (nextConditions) {
            dispatch(loanFetchedDataUwRequirementsSet(nextConditions));
          }
          setEditing(false);
        },
      });
    },
    [dealId, condition?._id, updateLoanUnderwritingRequirement, dispatch],
  );

  if (!condition?._id) return null;

  const hostLabel = condition?.docType
    ? `Condition · ${condition.docType}`
    : "Condition";

  return (
    <>
      <AssociationsDisplay
        loan={loan}
        associations={condition.associations || {}}
        onEdit={() => setEditing(true)}
      />
      <AssociationsEditor
        open={editing}
        loan={loan}
        initial={condition.associations || {}}
        hostLabel={hostLabel}
        onSave={handleSave}
        onClose={() => !saving && setEditing(false)}
        saving={saving}
      />
    </>
  );
};

/* ─── Delete condition (same two-step confirm) ─── */

const DeleteConditionSection = ({ loanId, requirementId }) => {
  const dispatch = useDispatch();
  const { loading, deleteLoanUnderwritingRequirement } =
    useChecklistRouteHook();
  const [confirming, setConfirming] = useState(false);

  const handleDelete = () => {
    deleteLoanUnderwritingRequirement({
      dealId: loanId,
      requirementId,
      onSuccessFn: (response) => {
        if (response?.conditions) {
          dispatch(loanFetchedDataUwRequirementsSet(response.conditions));
        }
        setConfirming(false);
        dispatch(sidebarClear());
      },
    });
  };

  if (isNil(loanId) || isNil(requirementId)) return null;

  if (!confirming) {
    return (
      <Flx>
        <Button
          size="small"
          variant="text"
          color="error"
          startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
          onClick={() => setConfirming(true)}
          sx={{ textTransform: "none" }}
        >
          Delete Condition
        </Button>
      </Flx>
    );
  }
  return (
    <Box
      sx={{
        background: red[50],
        border: `1px solid ${red[200]}`,
        borderRadius: 2,
        p: 1.5,
      }}
    >
      <Flx column g={1}>
        <Txt sx={{ fontSize: 12, fontWeight: 600, color: red[900] }}>
          Delete this condition?
        </Txt>
        <Txt sx={{ fontSize: 11, color: red[900] }}>
          This action can be undone from the deleted requirements list.
        </Txt>
        <Flx gap={1} ac>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => setConfirming(false)}
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="error"
            startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
            onClick={handleDelete}
            disabled={loading}
            sx={{ textTransform: "none" }}
          >
            {loading ? "Deleting…" : "Yes, Delete"}
          </Button>
        </Flx>
      </Flx>
    </Box>
  );
};

const CollapsibleCard = ({ sx, children }) => {
  const styles = useMemo(() => {
    return {
      overflow: "hidden",
      p: 0,
      ".sidebar-section-header": {
        px: 2,
        py: 1.5,
        mb: 0,
      },
      ".sidebar-section-body": {
        px: 2,
        py: 1.5,
      },
    };
  }, []);
  return <SectionCard sx={{ ...styles, ...sx }}>{children}</SectionCard>;
};

/* ─── SectionCard wrapper ─── */

export default DrilldownSidebarAddEditLoanConditionRedesign;
